// Which currency a commission policy is denominated in, and whether it
// can be paid at all (SM-497).
//
// ── the question this file answers ───────────────────────────────────
//
// A commission rate is a share of a fee the platform ALREADY COLLECTED,
// so its currency is never configured — it is the currency that fee was
// collected in, which is the book's own currency. That is why nothing on
// the Commission policies page picks a currency the way a money form
// does: the currency is derived, and the page's job is to SAY it rather
// than to ask for it.
//
//   Cash book → GOLD. One currency in, one out.
//   Game book → RED on a buy, GREEN on a sell or a redemption.
//
// The mapping is read from MODE_CURRENCIES rather than restated, so a
// fourth currency joining a book is one line there and this file, the
// filters and every label follow.
//
// ── SM-522 revised BOTH halves of this file ──────────────────────────
//
// A rate is now configured PER CURRENCY, not once per admin, so this
// file's job changed from "say which currency a line means" to "say
// which currency a line IS". A policy exists in Gold, in Red or in
// Green, and one says nothing about the others.
//
// ── the payability half, and why it is not cosmetic ──────────────────
//
// Until SM-522 a Game rate was stored, snapshotted and then priced at
// ZERO by settlement (SM-454), because no ADMIN.<id>.RED wallet existed
// anywhere and ledger fails CLOSED on a recipient it cannot resolve —
// taking the gross platform-fee leg with it. Ledger migration 0036 now
// provisions an admin commission account in every currency, and
// settlement prices each charge from the policy for the currency it was
// collected in, so a Red or Green rate ACCRUES.
//
// What still does not exist is a way to get it out. Admin withdrawal is
// Gold by construction — its request has no currency field — Red
// withdrawals are prohibited forever by the currency model, and
// conversion is player-scoped, so it never reaches an admin commission
// account. A Red or Green commission is therefore earnable and not yet
// withdrawable.
//
// That is a smaller gap than "accrues nothing" and a more dangerous one
// to leave unsaid: the balance is real, the obligation is real, and only
// the discharge is missing. So the console states accrual and payout
// separately and never lets one stand for the other.
import { MODE_CURRENCIES, type TradingMode } from "@/lib/modeCurrencies";
import { isCurrencyCode, type CurrencyCode } from "@/lib/currency";

/** Books a commission can be configured on, in the order they render. */
export const COMMISSION_BOOKS: readonly TradingMode[] = ["CASH", "GAME"] as const;

/**
 * Every currency a commission on this book could be earned in — the
 * book's own currencies, because a commission is carved from a fee that
 * book collected.
 */
export function commissionCurrencies(mode: TradingMode): CurrencyCode[] {
  return MODE_CURRENCIES[mode].currencies.filter(isCurrencyCode);
}

/** The books that could pay a commission in this currency — [] for none. */
export function booksForCommissionCurrency(code: string): TradingMode[] {
  return COMMISSION_BOOKS.filter((m) =>
    (MODE_CURRENCIES[m].currencies as readonly string[]).includes(code),
  );
}

/**
 * What happens to a commission earned in one currency: whether it
 * accrues at all, and whether the recipient can get it out.
 *
 * Two flags rather than one, because since SM-522 they differ. Every
 * currency accrues; only Gold has a payout rail. Collapsing them would
 * mean either hiding a real balance or claiming a rate earns nothing
 * when it does.
 *
 * `reason` is written as one whole sentence per idea so the localized
 * content bridge can extract it — see the SM-354 note in DESIGN.md.
 */
export interface CommissionAccrual {
  /** False when settlement prices every charge in this currency at zero. */
  accrues: boolean;
  /** False when nothing can move the balance out of the commission account. */
  payable: boolean;
  /** Present exactly when `accrues` or `payable` is false. */
  reason?: string;
}

// Written as whole sentences, and deliberately NOT starting with a bare
// "A " — `npm run i18n:check` reads a sentence-initial "A" as an
// ALL-CAPS technical token and rejects any translation that does not
// repeat it verbatim.
const NOT_PAYABLE_REASON =
  "Commission earned in this currency accrues into the recipient’s commission account and cannot be paid out yet. Admin payouts settle in Gold only, this currency has no withdrawal rail, and conversion reaches player wallets rather than commission accounts. Set a rate here when the intent is to record what is owed; the balance is real and the way to discharge it is not built.";

const CURRENCY_ACCRUAL: Readonly<Record<string, CommissionAccrual>> =
  Object.freeze({
    GOLD: Object.freeze({ accrues: true, payable: true }),
    RED: Object.freeze({ accrues: true, payable: false, reason: NOT_PAYABLE_REASON }),
    GREEN: Object.freeze({ accrues: true, payable: false, reason: NOT_PAYABLE_REASON }),
  });

/** What a commission earned in this currency does. */
export function currencyAccrual(code: string): CommissionAccrual {
  return (
    CURRENCY_ACCRUAL[code] ?? {
      accrues: false,
      payable: false,
      reason:
        "No book collects fees in this currency, so no commission can be earned in it.",
    }
  );
}

/**
 * The same question about a whole book, derived from its currencies
 * rather than restated — a book accrues if any of its currencies does,
 * and is payable only if all of them are.
 *
 * Kept for the market and event tabs, where the line under discussion
 * is one override covering every currency that book collects.
 */
export function commissionAccrual(mode: TradingMode): CommissionAccrual {
  const codes = commissionCurrencies(mode);
  const each = codes.map((c) => currencyAccrual(c));
  const accrues = each.some((a) => a.accrues);
  const payable = each.length > 0 && each.every((a) => a.payable);
  if (accrues && payable) return { accrues, payable };
  return {
    accrues,
    payable,
    reason: each.find((a) => a.reason)?.reason,
  };
}

/** The short form, for a badge beside a rate rather than a paragraph. */
export const NO_PAYOUT_BADGE = "No payout rail";

/**
 * Read a book out of untrusted text (a URL search param). Anything else
 * reads as NO filter: this value drives a control the operator can see,
 * and a URL the picker cannot represent would leave the list filtered
 * under a picker saying "All books".
 */
export function parseCommissionBook(raw: unknown): TradingMode | undefined {
  return typeof raw === "string" && COMMISSION_BOOKS.includes(raw as TradingMode)
    ? (raw as TradingMode)
    : undefined;
}

/**
 * Read a currency out of untrusted text, narrowed to currencies some
 * book actually trades in. A promo-only currency would filter every row
 * away with no way to tell that from a broken read.
 */
export function parseCommissionCurrency(raw: unknown): CurrencyCode | undefined {
  if (typeof raw !== "string" || !isCurrencyCode(raw)) return undefined;
  return booksForCommissionCurrency(raw).length > 0 ? raw : undefined;
}

/** The currencies the filter may name, in the contract's own order. */
export const COMMISSION_CURRENCIES: readonly CurrencyCode[] = Array.from(
  new Set(COMMISSION_BOOKS.flatMap(commissionCurrencies)),
);

/** What the two filters are narrowed to right now. */
export interface CommissionPolicyFilters {
  book?: TradingMode;
  currency?: CurrencyCode;
}

/**
 * Whether one book's policy line survives the filters.
 *
 * The two are ANDed rather than made to drive each other. A currency
 * filter that silently moved the book picker would answer a question the
 * operator did not ask, and the pair that selects nothing — Gold on the
 * Game book — is a real, explainable answer this screen would rather
 * state than paper over.
 */
export function bookMatchesFilters(
  mode: TradingMode,
  filters: CommissionPolicyFilters,
): boolean {
  if (filters.book && filters.book !== mode) return false;
  if (
    filters.currency &&
    !commissionCurrencies(mode).includes(filters.currency)
  ) {
    return false;
  }
  return true;
}

/** True when the two filters cannot both hold — no book is left. */
export function filtersContradict(filters: CommissionPolicyFilters): boolean {
  return !COMMISSION_BOOKS.some((m) => bookMatchesFilters(m, filters));
}

/**
 * The currencies a per-currency list should show under the two filters.
 *
 * Derived from the BOOKS the filters leave, then narrowed by the currency
 * filter — so "Game book" selects Red and Green without the operator
 * having to know which currencies that book collects, and an impossible
 * pair (Gold on the Game book) selects nothing rather than silently
 * widening to everything.
 *
 * Order is the contract's, so a list sorted by currency reads the same
 * way on every screen.
 */
export function visibleCurrencies(
  filters: CommissionPolicyFilters,
): CurrencyCode[] {
  const fromBooks = new Set(
    COMMISSION_BOOKS.filter((m) => !filters.book || filters.book === m).flatMap(
      commissionCurrencies,
    ),
  );
  return COMMISSION_CURRENCIES.filter(
    (c) => fromBooks.has(c) && (!filters.currency || filters.currency === c),
  );
}
